import { useState } from 'react'
import { BrowserRouter as Router } from 'react-router-dom'
import { Box, Grid, Button, TextField, IconButton, InputAdornment } from '@mui/material'
import { RemoveRedEyeOutlined as IRemoveRedEye, VisibilityOffOutlined as IVisibilityOff } from '@mui/icons-material'
import { env } from '../../core/Env'
import { rqx } from '../../core/request/API'
import { continueAfterAuth } from '../pwaShell'
import { pslstyle } from '../../core/styling/si'
import { SnakeLoader } from '../../core/loader/SnakeLoader'
import NavigationV2 from '../layouts/navigations_v2/Index'
import LPFSS from '../../assets/images/logos/pofsis_no_border.png'
import GSPFSS from '../../assets/images/logos/pofsis_grayscale.jpg'
import LPSL from '../../assets/images/logos/psl_logo.png'
import LBIZ from '../../assets/images/logos/biz_logo.png'

const parseLogo = (logo) => {
    if (!logo) return null
    try {
        return typeof logo === 'string' ? JSON.parse(logo) : logo
    } catch (err) {
        return null
    }
}

const normalizeGrantPlatform = (platform) => {
    if (platform === 'psl' || platform === 'personal') return 'psl'
    if (platform === 'biz' || platform === 'business') return 'biz'
    return null
}

/**
 * Provision default tool access after EntryAccess auth.
 * Body: { aid, platform: 'psl'|'biz', tid }. Success when API status === true.
 */
export const grantToolAccess = async ({ ainfo, platform, tid }) => {
    const aid = ainfo?.aid
    if (!aid) {
        return { msg: 'Missing account info' }
    }

    const normalized = normalizeGrantPlatform(platform)
    if (!normalized) {
        return { msg: 'Invalid platform' }
    }

    try {
        const response = await rqx.post(
            `<<global>>/${env() === 'local' ? 'dev' : env()}/tooluser/createUserToolAccess`,
            { aid, platform: normalized }
        )

        if (response?.status === true) {
            return { ...response, msg: 'success' }
        }

        return { msg: response?.msg || 'Unable to provision tool access' }
    } catch (err) {
        return { msg: `request failed: ${err?.message || err}` }
    }
}

/**
 * Plug-and-play: account exists but has no tool access.
 * Login (password) → createUserToolAccess → redirect to tool with ainfo.
 */
export const AddAccess = (props) => (
    <Router>
        <AddAccessForm {...props} />
    </Router>
)

const COPY = {
    login: {
        title: 'Verify it’s you',
        subtitle: (toolName) =>
            `For your security, re-enter your password to continue to ${toolName}.`,
        passwordLabel: 'Password',
        passwordHint: 'This extra step helps protect your account.',
        button: 'Continue',
    },
    signup: {
        title: 'Verify it’s you',
        subtitle: (toolName) =>
            `For your security, re-enter your password to finish and continue to ${toolName}.`,
        passwordLabel: 'Password',
        passwordHint: 'This extra step helps protect your account.',
        button: 'Continue',
    },
}

const AddAccessForm = ({
    email,
    account: accountProp,
    va = 'email',
    platform,
    apiPlatform,
    platformQuery,
    tool,
    authCallback,
    from,
    subcb,
    pricing,
    ref_a_frnd,
    mode = 'login',
}) => {
    const attemptPlatform = apiPlatform || platform
    const account = accountProp || email
    const classes = pslstyle(attemptPlatform)
    const copy = COPY[mode] || COPY.login
    const toolName = tool?.name || 'this tool'
    const [password, setPassword] = useState('')
    const [showPass, setShowPass] = useState(false)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')

    const toolLogo = parseLogo(tool?.logo) || (attemptPlatform === 'psl' ? LPSL : LBIZ)
    const accountLabel = va === 'mobile_num' ? 'Mobile' : 'Email'
    const displayAccount = va === 'mobile_num' ? `+63 ${account}` : account

    const persistSession = () => {
        const tinfo = tool
            ? [{ name: tool.name, tid: tool.tid, type: tool.type, logo: tool.logo }]
            : [{ name: 'Tool', tid: null, type: 1, logo: null }]

        const strg = {
            cb: authCallback,
            tid: tool?.tid ?? null,
            tinfo,
            expiration: '',
            platform: platformQuery || (attemptPlatform === 'biz' ? 'business' : 'personal'),
            tsa_key: from,
            pricing,
            subcb,
            ref_a_frnd,
        }
        localStorage.setItem('pm_tool_session', JSON.stringify(strg))
        return strg
    }

    const submit = async () => {
        if (busy) return
        setError('')

        if (!password) {
            setError('Password is required')
            return
        }
        if (!authCallback) {
            setError('Callback URL is missing')
            return
        }

        setBusy(true)
        try {
            const pass = password.includes('#') ? encodeURIComponent(password) : password
            const attempt = await rqx.get(
                `<<tool>>/${env()}/${attemptPlatform}/si/attempt/${va}`,
                { account, password: pass, ref: 2, platform: attemptPlatform }
            )

            if (attempt?.msg !== 'valid' || !attempt.ainfo) {
                setError(attempt?.msg === 'valid' ? 'Missing account info' : 'Incorrect password.')
                setBusy(false)
                return
            }

            const grant = await grantToolAccess({
                ainfo: attempt.ainfo,
                platform: attemptPlatform,
                tid: tool?.tid,
            })

            if (grant?.msg !== 'success') {
                setError(grant?.msg || 'Something went wrong. Please try again.')
                setBusy(false)
                return
            }

            persistSession()
            const params = new URLSearchParams()
            params.set('ainfo', JSON.stringify(attempt.ainfo))
            if (subcb) params.set('subcb', subcb)
            params.set('ref', 'auth')
            continueAfterAuth(`${authCallback}?${params.toString()}`, { platform: attemptPlatform })
        } catch (err) {
            setError(`request failed: ${err?.message || err}`)
            setBusy(false)
        }
    }

    return (
        <Box display="flex" flexDirection="column" justifyContent="space-between" alignItems="center" height="100%" minHeight="100vh">
            <Box borderRadius={5} p={3}>
                <Box
                    boxShadow="0px 0px 4px rgba(0, 0, 0, 0.04), 0px 8px 16px rgba(0, 0, 0, 0.08)"
                    bgcolor="#FFF"
                    borderRadius="8px"
                    px={4}
                    py={5}
                    width="auto"
                    height="auto"
                    minWidth="480px"
                >
                    <Grid container spacing={3}>
                        <Grid container flexDirection="column" item xs={12}>
                            <Box display="flex" justifyContent="center" alignItems="center" width="100%">
                                <Box p={1} display="flex" alignItems="center">
                                    <img src={LPFSS} alt="POFSIS Logo" width="52" />
                                </Box>
                                <Box
                                    className="personal-logo-px-1"
                                    ml={3}
                                    sx={{ backgroundImage: `url(${toolLogo})` }}
                                />
                            </Box>

                            <Box mt={3} fontSize="30px" fontWeight="600" align="center" color="#101828">
                                {copy.title}
                            </Box>
                            <Box mt={1.5} fontSize="16px" fontWeight="400" align="center" color="#5B6167">
                                {copy.subtitle(toolName)}
                            </Box>

                            <Box mb={2} mt={4} fontSize="14px" fontWeight="400" color="#292C2F">
                                <Box
                                    component="form"
                                    autoComplete="off"
                                    onSubmit={(e) => {
                                        e.preventDefault()
                                        submit()
                                    }}
                                >
                                    <Box mb={1} fontSize={13} color="#5B6167">{accountLabel}</Box>
                                    <Box
                                        fontSize={16}
                                        fontWeight={600}
                                        color={attemptPlatform === 'psl' ? 'primary.personal' : 'primary.business'}
                                        mb={2}
                                    >
                                        {displayAccount}
                                    </Box>

                                    <Box display="flex">
                                        {copy.passwordLabel}
                                        <Box color="red" ml={0.5} mb={1}>*</Box>
                                    </Box>
                                    <Box fontSize={13} color="#5B6167" mb={1}>{copy.passwordHint}</Box>
                                    <TextField
                                        size="small"
                                        variant="outlined"
                                        type={showPass ? 'text' : 'password'}
                                        value={password}
                                        disabled={busy}
                                        error={Boolean(error)}
                                        fullWidth
                                        className={`${classes.tf} ${classes.border}`}
                                        onChange={(e) => setPassword(e.target.value)}
                                        inputProps={{ maxLength: 50, autoComplete: 'new-password' }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPass((v) => !v)}>
                                                        {showPass ? <IRemoveRedEye /> : <IVisibilityOff />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Box display="flex" justifyContent="space-between">
                                        <Box color="error.main" fontSize={13} mt={0.5}>{error}</Box>
                                    </Box>

                                    <Button
                                        type="submit"
                                        size="large"
                                        variant="contained"
                                        className={classes.button}
                                        fullWidth
                                        disabled={busy}
                                        sx={{ marginBottom: '24px', marginTop: '16px' }}
                                    >
                                        {busy ? (
                                            <Box display="flex" alignItems="center" p={0.9}>
                                                <SnakeLoader bg="#ffffff" size="0.75rem" distance="0.3rem" />
                                            </Box>
                                        ) : (
                                            <Box display="flex" alignItems="center">{copy.button}</Box>
                                        )}
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <Box display="flex" alignItems="center">
                    <img src={GSPFSS} alt="POFSIS" width="14" />
                    <Box fontSize={16} fontWeight="600" color="#333333" ml={1}>POFSIS</Box>
                </Box>
                <Box color="#5B6167" fontSize="12px" mb={3}>One account for Personal , Business and more.</Box>
            </Box>
            <NavigationV2 side={1} />
        </Box>
    )
}
